"use client";

import React from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import {
  Layers,
  ChevronRight,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useSubcategoryPage } from "@/modules/public/home/hooks/usePublicData";
import SubcatProducts from "@/modules/public/home/components/productspage/SubcatProducts";

interface SubcategoryClientProps {
  categorySlug: string;
  subcatSlug: string;
}

export default function SubcategoryClient({ categorySlug, subcatSlug }: SubcategoryClientProps) {
  const router = useRouter();

  const {
    category,
    subcategory,
    products,
    filteredProducts,
    loading,
    error,
    searchQuery,
    setSearchQuery,
  } = useSubcategoryPage(categorySlug, subcatSlug);

  if (loading) {
    return (
      <div className="min-h-[75vh] flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="w-12 h-12 text-green-600 animate-spin mb-4" />
        <p className="text-gray-600 font-bold text-sm">Opening Subcategory Catalog...</p>
      </div>
    );
  }

  if (error || !category || !subcategory) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 bg-gray-55">
        <div className="bg-white p-8 rounded-xl shadow-xs border border-gray-200 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Catalog Sync Issue</h2>
          <p className="text-gray-655 mb-6">{error || "The subcategory you are looking for could not be loaded."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      {/* Breadcrumbs Bar */}
      <div className="bg-white border-b border-gray-200 py-3 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 overflow-x-auto whitespace-nowrap">
            <Link href="/" className="hover:text-green-600 transition-colors font-medium">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
            <Link href={`/${categorySlug}`} className="hover:text-green-600 transition-colors font-medium">
              {category.name}
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
            <span className="font-bold text-green-700 truncate max-w-xs">{subcategory.name}</span>
          </nav>
        </div>
      </div>

      {/* Subcategory Details Section (Hero) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          {/* Left side: Content */}
          <div className="md:col-span-7 space-y-4">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-green-600 tracking-tight leading-tight">
              {subcategory.title || subcategory.name}
            </h1>

            {/* Mobile Image (visible on mobile, hidden on desktop) */}
            <div className="block md:hidden w-full flex justify-center my-2">
              <div className="w-full max-w-[400px] aspect-4/3 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden relative group shadow-xs flex items-center justify-center">
                {subcategory.imageUrl ? (
                  <Image
                    src={subcategory.imageUrl}
                    alt={subcategory.name}
                    fill
                    sizes="(max-width: 768px) 100vw, 400px"
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <Layers className="w-20 h-20 text-gray-300 animate-pulse" />
                )}
              </div>
            </div>

            <p className="text-gray-800 text-sm sm:text-base leading-relaxed text-justify">
              {subcategory.description ||
                `Explore high-quality products and premium solutions under the ${subcategory.name} subcategory. Connect directly with verified vendors for details and quotes.`}
            </p>
          </div>

          {/* Right side: Image (Desktop only) */}
          <div className="hidden md:flex md:col-span-5 w-full h-full justify-center">
            <div className="w-full max-w-[400px] aspect-4/3 md:aspect-square bg-gray-50 rounded-xl border border-gray-200 overflow-hidden relative group shadow-xs flex items-center justify-center">
              {subcategory.imageUrl ? (
                <Image
                  src={subcategory.imageUrl}
                  alt={subcategory.name}
                  fill
                  sizes="(max-width: 768px) 100vw, 400px"
                  className="object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <Layers className="w-20 h-20 text-gray-300 animate-pulse" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <SubcatProducts
        products={products}
        filteredProducts={filteredProducts}
        subcategory={subcategory}
        category={category}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    </div>
  );
}
